import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, useTheme, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { membershipAPI, notificationAPI } from '../../actions/imsServer';
import { toDisplayString, formatDateTimeDisplay } from '../../utils/base';
import dayjs from 'dayjs';
import Dropdown from '../../components/dropdown';
import ContentArea from '../../components/contentArea';
import { LevelUpIcon, RewardIcon, ChampionshipIcon, NoticeIcon } from '../../assets/images/notificationIcons';
import { API_PAGE_SIZE } from '../../constants';

const ITEMS_PER_PAGE = API_PAGE_SIZE;

export const NoticeNotification = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [notifications, setNotifications] = useState([]); // 표시할 알림 목록
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const listRef = useRef(null);

    const meInfo = JSON.parse(localStorage.getItem('meInfo'));
    const [tab, setTab] = useState('');
    const [userOrganizationIds, setUserOrganizationIds] = useState([]);
    const [orgsLoaded, setOrgsLoaded] = useState(false);
    const tabList = [
        { label: t('all'), value: '' },
        { label: t('level_up'), value: 'levelup' },
        { label: t('reward'), value: 'reward' },
        { label: t('championship'), value: 'champion' },
        { label: t('notices'), value: 'notification,event' },
    ];

    const [period, setPeriod] = useState('1m');
    const periodOptions = [
        { label: t('last_1_month'), value: '1m' },
        { label: t('last_3_months'), value: '3m' },
        { label: t('view_all'), value: 'all' },
    ];

    // 기간에 따른 최소 날짜 계산
    const getMinDate = useCallback((periodValue) => {
        const now = dayjs();
        switch (periodValue) {
            case '1m':
                return now.subtract(1, 'month').format('YYYY-MM-DD');
            case '3m':
                return now.subtract(3, 'month').format('YYYY-MM-DD');
            case 'all':
                return null; // 전체 기간은 날짜 필터 없음
            default:
                return now.subtract(1, 'month').format('YYYY-MM-DD');
        }
    }, []);

    // 내 소속 기관 목록 로드
    useEffect(() => {
        const fetchOrganizations = async () => {
            if (!meInfo?.id) {
                setUserOrganizationIds([]);
                setOrgsLoaded(true);
                return;
            }
            try {
                const membershipsResponse = await membershipAPI.getMemberships({
                    userId: meInfo.id,
                    type: 1,
                    state: 0,
                });
                const memberships =
                    membershipsResponse?.data?.memberships ||
                    membershipsResponse?.memberships ||
                    (Array.isArray(membershipsResponse) ? membershipsResponse : []);
                const ids = [...new Set(memberships.map(m => m.organizationId).filter(id => id != null))];
                setUserOrganizationIds(ids);
                setOrgsLoaded(true);
            } catch (err) {
                setUserOrganizationIds([]);
                setOrgsLoaded(true);
            }
        };

        setOrgsLoaded(false);
        fetchOrganizations();
    }, [meInfo?.id]);

    // 알림 데이터 가져오기 (서버 사이드 필터링)
    const fetchNotifications = useCallback(async (reset = false, currentOffsetValue = 0) => {
        if (!orgsLoaded) {
            return;
        }
        if (isLoading || isLoadingMore) return;

        const currentOffset = reset ? 0 : currentOffsetValue;

        if (reset) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }

        try {
            const minDate = getMinDate(period);

            // API 파라미터 준비
            const params = {
                state: '0,2',
                offset: currentOffset,
                limit: ITEMS_PER_PAGE,
                // 내 소속 기관(들) + 전체(0) 포함
                ...(meInfo?.id ? { organizationId: [0, ...userOrganizationIds].join(',') } : {}),
            };

            if (tab !== '') {
                params.filter = tab;
            }
            // 기간 필터 (서버 사이드)
            if (minDate) {
                params.startDate = minDate;
            }

            // 사용자별 필터
            if (meInfo.grade === 1) {
                params.userId = meInfo.id;
            } else {
                params.userId = 0;
            }

            const response = await notificationAPI.getNotifications(params);
            const notificationsData = Array.isArray(response)
                ? response
                : response?.data?.notifications || response?.notifications || [];

            const total = response?.data?.total || response?.total || 0;

            if (reset) {
                setNotifications(notificationsData);
                setOffset(notificationsData.length);
            } else {
                setNotifications(prev => {
                    // 중복 제거
                    const combined = [...prev, ...notificationsData];
                    const unique = combined.filter(
                        (notification, index, self) =>
                            index === self.findIndex(n => n.id === notification.id)
                    );
                    return unique;
                });
                setOffset(prev => prev + notificationsData.length);
            }

            // 더 불러올 데이터가 있는지 확인
            const newOffset = reset ? notificationsData.length : currentOffset + notificationsData.length;
            setHasMore(newOffset < total && notificationsData.length === ITEMS_PER_PAGE);
        } catch (e) {
            console.error('Error fetching notifications:', e);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [tab, period, isLoading, isLoadingMore, meInfo?.id, meInfo?.grade, getMinDate, userOrganizationIds, orgsLoaded]);

    // 탭 변경 핸들러
    const handleTabChange = useCallback((newTab) => {
        setTab(newTab);
        setOffset(0);
        setHasMore(true);
    }, []);

    // 기간 변경 핸들러
    const handlePeriodChange = useCallback((newPeriod) => {
        setPeriod(newPeriod);
        setOffset(0);
        setHasMore(true);
    }, []);

    // 초기 로드 및 필터 변경 시 재로드
    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        if (!orgsLoaded) return;
        fetchNotifications(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchNotifications는 의존성에서 제외(반복 호출 방지)
    }, [tab, period, meInfo?.grade, meInfo?.id, orgsLoaded]);

    // 무한 스크롤 핸들러
    const handleScroll = useCallback((e) => {
        const target = e.target;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // 스크롤이 하단 근처(100px 이내)에 도달했을 때
        if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !isLoadingMore && !isLoading) {
            fetchNotifications(false, offset);
        }
    }, [hasMore, isLoadingMore, isLoading, fetchNotifications, offset]);

    // 아이콘 타입
    const getIcon = filter => {
        if (filter === 'levelup') {
            // 레벨업
            return <LevelUpIcon size={48} opacity={0.6} />;
        } else if (filter === 'reward') {
            // 리워드
            return <RewardIcon size={48} opacity={0.6} />;
        } else if (filter === 'champion') {
            // 챔피언십
            return <ChampionshipIcon size={48} opacity={0.6} />;
        } else {
            // 공지사항 (notification, event)
            return <NoticeIcon size={48} opacity={0.6} />;
        }
    };


    const handleNoticeClick = notice => {
        if (notice.filter === "notification") {
            navigate(`/notices/notice-list`);
        } else if (notice.filter === "reward") {
            navigate(`/rewards/challenge`);
        } else if (notice.filter === "champion") {
            navigate(`/championships/user`);
        } else if (notice.filter === "event") {
            navigate(`/events/user`);
        } else {
            navigate('/');
        }
    };

    return (
        <ContentArea
            sx={{ gap: '0px', overflow: 'hidden' }}
        >
            {/* Filter Buttons */}
            <Box
                sx={{
                    display: 'flex',
                    gap: '10px',
                    padding: '10px 0px 10px 10px',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid ' + theme.palette.figma?.white?.[30] || 'rgba(255, 255, 255, 0.3)',
                    alignSelf: 'stretch',
                }}
            >
                {tabList.map(item => (
                    <Box
                        key={item.value}
                        onClick={() => handleTabChange(item.value)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '5px',
                            padding: '5px 10px',
                            borderRadius: '10px',
                            flexShrink: 0,
                            width: 'fit-content',
                            whiteSpace: 'nowrap',
                            background:
                                tab === item.value
                                    ? theme.palette.figma.buttonOrange
                                    : theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                            color: tab === item.value ? theme.palette.figma?.buttonText : theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                            fontSize: '14px',
                            fontWeight: tab === item.value ? 600 : 400,
                            letterSpacing: '-0.42px',
                            cursor: 'pointer',
                            transition: 'background 0.2s, color 0.2s',
                            textAlign: 'center',
                        }}
                    >
                        {toDisplayString(item.label, item.value)}
                    </Box>
                ))}
            </Box>

            {/* Period Select */}
            <Box
                sx={{
                    display: 'flex',
                    padding: '10px',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '10px',
                    alignSelf: 'stretch',
                    borderBottom: '1px solid ' + theme.palette.figma?.white?.[30] || 'rgba(255, 255, 255, 0.3)',
                }}
            >
                <Dropdown
                    value={period}
                    onChange={option => handlePeriodChange(option.value)}
                    options={periodOptions}
                    placeholder={t('last_1_month')}
                    sx={{ backgroundColor: theme.palette.figma.block }}
                />
            </Box>
            {/* List */}
            <List
                ref={listRef}
                onScroll={handleScroll}
                sx={{
                    display: 'flex',
                    padding: 0,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    flex: 1,
                    minHeight: 0,
                    alignSelf: 'stretch',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    backgroundColor: 'transparent',
                }}
            >
                {
                    notifications.length === 0 && !isLoading ? (
                        <ListItem
                            sx={{
                                display: 'flex',
                                minWidth: 0,
                                padding: '20px',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '16px',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Typography sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }}>
                                {t('no_notifications')}
                            </Typography>
                        </ListItem>
                    ) : (
                        <>
                            {notifications.map((notice) => (
                                <ListItem
                                    key={notice.id}
                                    sx={{
                                        minWidth: 0,
                                        alignSelf: 'stretch',
                                        borderBottom: '1px dashed ' + theme.palette.figma?.white?.[30] || 'rgba(255, 255, 255, 0.3)',
                                        backgroundColor: 'transparent',
                                    }}
                                    disablePadding
                                >
                                    <ListItemButton
                                        onClick={() => handleNoticeClick(notice)}
                                        sx={{
                                            display: 'flex',
                                            minWidth: 0,
                                            padding: '20px 10px',
                                            alignItems: 'center',
                                            gap: '10px',
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 48,
                                                height: 48,
                                                flexShrink: 0,
                                                opacity: 0.6,
                                            }}
                                        >
                                            {getIcon(notice.filter)}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                flex: 1,
                                                minWidth: 0,
                                                gap: '10px',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                                                    lineHeight: 'normal',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'normal',
                                                    textAlign: 'left',
                                                    letterSpacing: '-0.42px',
                                                }}
                                            >
                                                {notice.content}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)',
                                                    fontSize: '12px',
                                                    fontWeight: 400,
                                                    lineHeight: 'normal',
                                                    textAlign: 'left',
                                                    letterSpacing: '-0.36px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {formatDateTimeDisplay(notice.startDate, 'date')}
                                            </Typography>
                                        </Box>
                                    </ListItemButton>
                                </ListItem>
                            ))}
                            {isLoadingMore && (
                                <ListItem
                                    sx={{
                                        display: 'flex',
                                        minWidth: 0,
                                        padding: '20px',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <CircularProgress size={24} sx={{ color: theme.palette.figma?.white?.[60] || 'rgba(255, 255, 255, 0.6)' }} />
                                </ListItem>
                            )}
                        </>
                    )
                }
            </List >
        </ContentArea >
    );
};

export default NoticeNotification;
